import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { GetChannelIntegrationsDocument } from 'graphql-query-contracts';
import { IntegrationProviderEnum } from 'constants-integrations';

export type IntercomConfig = { appId: string };

type UseIntercomConfigResult = {
  intercomConfig: IntercomConfig | null;
  channelIntegrationsLoaded: boolean;
};

export function useIntercomConfig(
  channelId: string | undefined,
  enabled: boolean
): UseIntercomConfigResult {
  const { data } = useQuery(GetChannelIntegrationsDocument, {
    variables: { id: channelId },
    skip: !enabled || !channelId,
  });

  const isRequestedChannelData = data?.channel?._id === channelId;

  const intercomConfig = useMemo(() => {
    if (!isRequestedChannelData) return null;

    if (!data?.channel?.integrations) return null;

    const intercom = data.channel.integrations.find((ci) => {
      return ci.integration?.name === IntegrationProviderEnum.Intercom;
    });

    if (!intercom?.settings) return null;

    const appId = intercom.settings.appId;

    if (typeof appId !== 'string' || !appId) return null;

    return { appId };
  }, [data, isRequestedChannelData]);

  const channelIntegrationsLoaded =
    isRequestedChannelData && Array.isArray(data?.channel?.integrations);

  return {
    intercomConfig,
    channelIntegrationsLoaded,
  };
}

